/**
 * src/api/vault.js — Career Vault API client.
 *
 * All requests use relative URLs so they work via the existing
 * React dev server proxy (http://localhost:3000 → http://localhost:5000).
 *
 * Public URL pattern:
 *   Per-doc share:  /api/vault/public/<uuid>           (no auth, if is_public)
 *   Profile page:   /career/<slug>                     (React route, fetches /api/vault/profile/<slug>)
 *   Profile meta:   /api/vault/profile/meta            (auth, GET/POST career title/about/skills)
 */

const BASE = "/api/vault";

function authHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
  };
}

const vaultApi = {
  /**
   * List all vault documents for the current user.
   * @param {string} token
   * @param {string} [category] - optional category filter
   */
  async list(token, category = null) {
    const url = category ? `${BASE}?category=${category}` : BASE;
    const res = await fetch(url, { headers: authHeaders(token) });
    if (!res.ok) throw new Error("Failed to load vault documents.");
    return res.json(); // { documents: [...] }
  },

  /**
   * Upload a new vault document.
   * @param {string} token
   * @param {File} file
   * @param {{ title, category, notes, is_public }} meta
   * @param {function} [onProgress] - optional (0-100)
   */
  async upload(token, file, meta = {}, onProgress = null) {
    const form = new FormData();
    form.append("file", file);
    form.append("title", meta.title || file.name);
    form.append("category", meta.category || "other");
    form.append("notes", meta.notes || "");
    form.append("is_public", meta.is_public ? "true" : "false");

    // Use XMLHttpRequest for progress tracking, fall back to fetch
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE}/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(JSON.parse(xhr.responseText)?.error || "Upload failed."));
          }
        };
        xhr.onerror = () => reject(new Error("Upload network error."));
        xhr.send(form);
      });
    }

    const res = await fetch(`${BASE}/upload`, {
      method: "POST",
      headers: authHeaders(token),
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Upload failed.");
    }
    return res.json(); // { ok: true, document: {...} }
  },

  /**
   * Update document metadata (title, notes, category, is_public).
   * @param {string} token
   * @param {number} id
   * @param {object} patch
   */
  async update(token, id, patch) {
    const res = await fetch(`${BASE}/${id}`, {
      method: "PATCH",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error("Failed to update document.");
    return res.json(); // { ok: true, document: {...} }
  },

  /**
   * Delete a vault document.
   * @param {string} token
   * @param {number} id
   */
  async remove(token, id) {
    const res = await fetch(`${BASE}/${id}`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error("Failed to delete document.");
    return res.json(); // { ok: true }
  },

  /**
   * Get the authenticated download URL for a vault document.
   * Returns a blob URL so it works cross-origin.
   * @param {string} token
   * @param {number} id
   * @param {string} filename
   */
  async download(token, id, filename) {
    const res = await fetch(`${BASE}/${id}/download`, {
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error("Download failed.");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },

  /**
   * Get the QR code PNG blob URL for a document (optional download feature).
   * @param {string} token
   * @param {number} id
   */
  async getQrBlobUrl(token, id) {
    const res = await fetch(`${BASE}/${id}/qr`, {
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error("Failed to generate QR code.");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Build the public share URL for a document.
   * Returns the URL that can be copied and shared with recruiters.
   * @param {string} publicUuid
   */
  getPublicUrl(publicUuid) {
    return `${window.location.origin}/api/vault/public/${publicUuid}`;
  },

  /**
   * Build the public profile URL for a user (recruiter page).
   * @param {string} slug
   */
  getProfileUrl(slug) {
    return `${window.location.origin}/career/${slug}`;
  },

  /**
   * Fetch public vault profile data (for VaultPublicPage — no auth).
   * Returns full profile: display_name, picture, career_title, career_about,
   * career_skills, documents, grouped, total_documents.
   * @param {string} slug
   */
  async fetchProfile(slug) {
    const res = await fetch(`${BASE}/profile/${slug}`);
    if (!res.ok) throw new Error("Profile not found.");
    return res.json();
  },

  /**
   * Get the current user's career profile meta (title, about, skills, picture).
   * @param {string} token
   */
  async getProfileMeta(token) {
    const res = await fetch(`${BASE}/profile/meta`, { headers: authHeaders(token) });
    if (!res.ok) throw new Error("Failed to load profile meta.");
    return res.json(); // { name, email, picture, career_title, career_about, career_skills[] }
  },

  /**
   * Update the current user's career profile meta.
   * @param {string} token
   * @param {{ career_title, career_about, career_skills }} meta
   */
  async updateProfileMeta(token, meta) {
    const res = await fetch(`${BASE}/profile/meta`, {
      method: "POST",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify(meta),
    });
    if (!res.ok) throw new Error("Failed to update profile meta.");
    return res.json(); // { ok: true }
  },

  // ── Vault References (resume section linking) ───────────────────────────

  async listReferences(token) {
    const res = await fetch(`${BASE}/references`, { headers: authHeaders(token) });
    if (!res.ok) throw new Error("Failed to load vault references.");
    return res.json();
  },

  async createReference(token, { vault_asset_id, label, section_type, section_key, display_type }) {
    const res = await fetch(`${BASE}/references`, {
      method: "POST",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify({ vault_asset_id, label, section_type, section_key, display_type }),
    });
    if (!res.ok) throw new Error("Failed to create vault reference.");
    return res.json();
  },

  async deleteReference(token, refId) {
    const res = await fetch(`${BASE}/references/${refId}`, {
      method: "DELETE",
      headers: authHeaders(token),
    });
    if (!res.ok) throw new Error("Failed to delete vault reference.");
    return res.json();
  },
};

export default vaultApi;
